import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Warning } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { BillingIdentityFields, type BillingIdentityValue } from "@/components/enrollment/BillingIdentityFields";
import { PayoutMethodField } from "@/components/enrollment/PayoutMethodField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { apiGet, apiSend } from "@/lib/api-error";
import { BANK_ACCOUNT_ERROR, isValidBankAccount, normalizeBankAccount } from "@/lib/bank-account";
import type { TherapistProfile, PayoutMethod } from "@/types";

export function SubscriptionBanner() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);
  const [billing, setBilling] = useState<BillingIdentityValue>({});
  const [payoutMethod, setPayoutMethod] = useState<PayoutMethod>("invoice");
  const [billingError, setBillingError] = useState<string | null>(null);

  const { data: subscription } = useQuery({
    queryKey: ["me", "subscription"],
    queryFn: async () => {
      const res = await fetch("/api/me/subscription");
      if (!res.ok) return null;
      return res.json() as Promise<{ state: "exempt" | "active" | "expired" | "never_paid" | null; expiresAt: string | null }>;
    },
  });

  const { data: profile, isLoading: profileLoading } = useQuery<TherapistProfile>({
    queryKey: ["therapist", "profile", "me"],
    queryFn: () => apiGet<TherapistProfile>("/api/therapist/me", t("therapist.profile.errorLoading")),
    enabled: billingDialogOpen,
  });

  const renewMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/me/subscription/renew-link", { method: "POST" });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = body && typeof body.error === "string" ? body.error : t("therapist.dashboard.subscriptionRenewError");
        throw new Error(msg);
      }
      return body as { url: string };
    },
    onSuccess: (body) => {
      window.location.href = body.url;
    },
  });

  const saveBillingMutation = useMutation({
    mutationFn: async (value: BillingIdentityValue) => {
      if (!profile?.id) throw new Error(t("therapist.dashboard.subscriptionBillingProfileMissing"));
      const payload = buildBillingPayload(payoutMethod, value, t);
      await apiSend(`/api/therapists/${profile.id}`, "PATCH", payload, t("therapist.profile.errorSaving"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["therapist", "profile", "me"] });
    },
  });

  useEffect(() => {
    if (!profile || !billingDialogOpen) return;
    setPayoutMethod(profile.payoutMethod);
    setBilling({
      businessName: profile.businessName ?? "",
      businessAddress: profile.businessAddress ?? "",
      businessTaxId: profile.businessTaxId ?? "",
      businessVatId: profile.businessVatId ?? "",
      personalAddress: profile.personalAddress ?? "",
      personalTaxId: profile.personalTaxId ?? "",
      bankName: profile.bankName ?? "",
      bankAccount: profile.bankAccount ?? "",
    });
  }, [profile, billingDialogOpen]);

  const showSubscription =
    !!subscription &&
    subscription.state !== "active" &&
    subscription.state !== "exempt" &&
    subscription.state !== null;
  const isExpired = subscription?.state === "expired";
  const error = renewMutation.error instanceof Error ? renewMutation.error.message : null;
  const billingInfoMissing = error?.startsWith("BILLING_INFO_MISSING");

  const handleRenewClick = async () => {
    setBillingError(null);
    try {
      await renewMutation.mutateAsync();
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.startsWith("BILLING_INFO_MISSING")) {
        setBillingDialogOpen(true);
      }
    }
  };

  const handleSaveBillingAndRenew = async () => {
    setBillingError(null);
    try {
      await saveBillingMutation.mutateAsync(billing);
      await renewMutation.mutateAsync();
    } catch (err) {
      const message = err instanceof Error ? err.message : t("therapist.dashboard.subscriptionBillingSaveError");
      setBillingError(message.startsWith("BILLING_INFO_MISSING") ? t("therapist.dashboard.subscriptionBillingMissing") : message);
    }
  };

  if (!showSubscription) {
    return null;
  }

  return (
    <div className="w-full shrink-0 bg-neutral-900 shadow-sm">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 py-3 text-white sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div className="flex min-w-0 items-start gap-3 sm:items-center">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-orange-500/15 text-orange-400">
            <Warning className="size-4" weight="fill" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-snug">
              {isExpired ? t("therapist.dashboard.subscriptionExpired") : t("therapist.dashboard.subscriptionNeverPaid")}
            </p>
            <p className="mt-0.5 text-sm leading-snug text-white/85">
              {t("therapist.dashboard.subscriptionRenewHint")}
            </p>
            {error && !billingInfoMissing && (
              <p className="mt-1 text-sm font-medium text-white">{error}</p>
            )}
          </div>
        </div>

        <Button
          type="button"
          size="sm"
          onClick={handleRenewClick}
          disabled={renewMutation.isPending}
          className="w-auto shrink-0 self-start border-0 bg-white font-semibold text-neutral-900 shadow-sm hover:bg-white/90 sm:self-auto"
        >
          {renewMutation.isPending ? t("common.loading") : t("therapist.dashboard.subscriptionRenewCta")}
          {!renewMutation.isPending && <ArrowRight className="size-4 text-neutral-900" weight="bold" />}
        </Button>
      </div>

      <Dialog open={billingDialogOpen} onClose={() => setBillingDialogOpen(false)} className="sm:max-w-lg">
        <DialogHeader onClose={() => setBillingDialogOpen(false)}>
          <DialogTitle>{t("therapist.dashboard.subscriptionBillingDialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("therapist.dashboard.subscriptionBillingDialogDescription")}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {profileLoading || !profile ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : (
            <>
              {!profile.isVolunteer && (
                <PayoutMethodField
                  value={payoutMethod}
                  onChange={(next) => {
                    setPayoutMethod(next);
                    setBillingError(null);
                  }}
                  disabled={saveBillingMutation.isPending || renewMutation.isPending}
                />
              )}
              <BillingIdentityFields
                payoutMethod={payoutMethod}
                isVolunteer={profile.isVolunteer}
                value={billing}
                onChange={(next) => {
                  setBilling(next);
                  setBillingError(null);
                }}
                bankRequired={false}
              />
            </>
          )}
          {billingError && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {billingError}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setBillingDialogOpen(false)}
            disabled={saveBillingMutation.isPending || renewMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSaveBillingAndRenew}
            disabled={profileLoading || !profile || saveBillingMutation.isPending || renewMutation.isPending}
          >
            {saveBillingMutation.isPending || renewMutation.isPending ? t("common.loading") : t("therapist.dashboard.subscriptionBillingSaveCta")}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

function buildBillingPayload(
  payoutMethod: PayoutMethod,
  value: BillingIdentityValue,
  t: ReturnType<typeof useTranslation>["t"],
): Record<string, string> {
  const clean = (v: string | undefined) => v?.trim() ?? "";

  // Račun za isplatu ovdje nije obavezan (pretplata ne zavisi od njega), ali ako
  // je upisan šalje se, a neispravan broj se javlja umjesto da se tiho izgubi.
  const bank: Record<string, string> = {};
  const bankName = clean(value.bankName);
  const bankAccount = clean(value.bankAccount);
  if (bankName.length >= 2) bank.bankName = bankName;
  if (bankAccount) {
    if (!isValidBankAccount(normalizeBankAccount(bankAccount))) throw new Error(`${BANK_ACCOUNT_ERROR}.`);
    bank.bankAccount = bankAccount;
  }

  // Persist the chosen method alongside the fields so the renew-link builder
  // reads the branch the therapist just selected (not a stale profile value).
  if (payoutMethod === "invoice") {
    const businessName = clean(value.businessName);
    const businessAddress = clean(value.businessAddress);
    const businessTaxId = clean(value.businessTaxId);
    const businessVatId = clean(value.businessVatId);
    if (!businessName || !businessAddress || !businessTaxId) {
      throw new Error(t("therapist.dashboard.subscriptionBillingRequiredFields"));
    }
    if (!/^\d{13}$/.test(businessTaxId)) {
      throw new Error(t("therapist.dashboard.subscriptionBillingTaxIdInvalid"));
    }
    return { payoutMethod, businessName, businessAddress, businessTaxId, businessVatId, ...bank };
  }

  const personalAddress = clean(value.personalAddress);
  const personalTaxId = clean(value.personalTaxId);
  if (!personalAddress || !personalTaxId) {
    throw new Error(t("therapist.dashboard.subscriptionBillingRequiredFields"));
  }
  if (!/^\d{13}$/.test(personalTaxId)) {
    throw new Error(t("therapist.dashboard.subscriptionBillingTaxIdInvalid"));
  }
  return { payoutMethod, personalAddress, personalTaxId, ...bank };
}
